// PlacesPage.jsx — CONTAINER (dono da lógica). Monta { vm, actions } reais e renderiza a PlacesView
// (View pura do Claude Design). Formato View/ViewModel à risca (docs/DESIGN-FLOW.md).
// O visual é da View; aqui há estado, chamadas à API e os dialogs (que continuam os de antes).
// A tradução para o vm mora em places/placesVmBuilder.js (função pura, coberta por
// places/__tests__/placesVmBuilder.test.js).
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import placesService from '../services/placesService';
import photoService from '../services/photoService';
import usePlacesCRUD from '../hooks/usePlacesCRUD';
import { useSchedules, useEntities } from '../hooks/queries';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { PlacesDataProvider } from '../contexts/PlacesDataContext';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';

import AppSnackbar from '../components/ui/AppSnackbar';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PlaceForm from '../components/forms/PlaceForm';
import PlaceServicePaymentDialog from '../components/places/PlaceServicePaymentDialog';
import PlacePaymentsManagerDialog from '../components/places/PlacePaymentsManagerDialog';
import PlacePaymentCascadeConfirmDialog from '../components/places/PlacePaymentCascadeConfirmDialog';
import PlaceServicesManager from '../components/places/PlaceServicesManager';
import PlaceProfessionalsManager from '../components/places/PlaceProfessionalsManager';
import ProfessionalServicesDialog from '../components/places/ProfessionalServicesDialog';
import SmartWizard from '../components/wizard/SmartWizard';
import AppointmentFormDialog from '../components/AppointmentFormDialog';

import PlacesView from './places/PlacesView';
import { buildPlacesVm } from './places/placesVmBuilder';

const fotoDe = (donoId, profissionalId) => (donoId ? photoService.getProfilePhotoUrl(donoId, profissionalId) : '');

// missingFields[].key (contrato) → campo do PlaceForm que recebe o foco.
const CAMPO_DO_FORM = { address: 'addressStreet', phone: 'phone', email: 'email', website: 'website', description: 'description' };

export default function PlacesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getLabel } = useThemeCustom();

  // Três leituras: a lista de locais (com preços, movimento e páginas), as agendas com as
  // métricas, e o espelho com turnos e ausências. O veredito só sai com as três na mão.
  const { data, loading: carregandoLocais, error: erroLocais, reload: recarregarLocais, create, update } = usePlacesCRUD(placesService);
  const { schedules, loading: carregandoAgendas, error: erroAgendas } = useSchedules();
  // refetchOnMount: quem volta de Agendas depois de mexer num turno precisa ver o veredito novo.
  const { entities, loading: carregandoEspelho, error: erroEspelho } = useEntities({ queryOptions: { refetchOnMount: 'always' } });

  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const paymentDialog = useDialog();
  const servicesDialog = useDialog();
  const professionalsDialog = useDialog();
  const professionalServicesDialog = useDialog();
  const placePaymentsDialog = useDialog();
  const placePaymentsCascadeDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const [showSmartWizard, setShowSmartWizard] = useState(false);
  const [agendamento, setAgendamento] = useState(null); // prefillConfig do AppointmentFormDialog
  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState('name_asc');
  const [segment, setSegment] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [campoEmFoco, setCampoEmFoco] = useState(null);

  // ?wizard=1 / ?create=1 abrem o cadastro (links do onboarding e da Home).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('wizard') === '1' || params.get('wizard') === 'true' || params.get('create') === '1') {
      setShowSmartWizard(true);
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location.pathname, location.search]);

  // Vincular ou desvincular serviço/profissional mexe nas agendas e no espelho também.
  const recarregarTudo = useCallback(() => {
    recarregarLocais();
    queryClient.invalidateQueries({ queryKey: ['professionals'] });
    queryClient.invalidateQueries({ queryKey: ['schedules'] });
    queryClient.invalidateQueries({ queryKey: ['entities'] });
  }, [recarregarLocais, queryClient]);

  // O tema devolve a própria chave quando não tem o rótulo ("placesExamples") — isso não é texto.
  const rotulo = useCallback((k) => { const v = getLabel(k); return v && v !== k ? v : null; }, [getLabel]);

  const vm = useMemo(() => buildPlacesVm({
    data,
    schedules,
    entities: entities || null,
    getLabel: rotulo,
    filters: { searchText, sortOrder, segment },
    selectedId,
    loading: carregandoLocais || carregandoAgendas || carregandoEspelho,
    error: erroLocais || erroAgendas || erroEspelho,
    fotoDe,
  }), [data, schedules, entities, rotulo, searchText, sortOrder, segment, selectedId,
    carregandoLocais, carregandoAgendas, carregandoEspelho, erroLocais, erroAgendas, erroEspelho]);

  // O local aberto foi excluído (ou sumiu na recarga): volta para a lista.
  useEffect(() => {
    if (selectedId && !carregandoLocais && !(data.places || []).some((p) => p.id === selectedId)) setSelectedId(null);
  }, [selectedId, carregandoLocais, data.places]);

  const acharLocal = (id) => (data.places || []).find((p) => p.id === id);
  const vinculoDoProfissional = (place, professionalId) => {
    const link = (place?.professionals || []).find((l) => l.professionalId === professionalId);
    if (!link) return null;
    const professional = (data.professionals || []).find((p) => p.id === professionalId);
    return { ...link, professional, professionalName: professional?.name };
  };
  const agendaPorId = (id) => (schedules || []).find((s) => s.id === id);

  const handleSubmit = async (valores) => {
    try {
      if (formDialog.isEdit) {
        await update(formDialog.data.id, valores);
        showSuccess(`Alterações em "${valores.name || formDialog.data.name}" salvas.`);
      } else {
        await create(valores);
        showSuccess('Cadastro criado.');
      }
      formDialog.handleClose();
    } catch (err) {
      showError(`Não foi possível salvar: ${err.response?.data?.message || err.message}`);
    }
  };

  const abrirAgendas = (scheduleId) => {
    const agenda = agendaPorId(scheduleId);
    navigate(agenda ? `/schedules?busca=${encodeURIComponent(agenda.name)}` : '/schedules');
  };

  const actions = {
    onRetry: recarregarTudo,
    onSearchChange: setSearchText,
    onSortChange: setSortOrder,
    onSegmentChange: setSegment,
    onClearFilters: () => { setSearchText(''); setSegment('all'); },
    onNew: () => setShowSmartWizard(true),
    onOpen: (id) => setSelectedId(id),
    onCloseDetail: () => setSelectedId(null),
    onEdit: (id) => { const p = acharLocal(id); if (p) { setCampoEmFoco(null); formDialog.handleOpen(p); } },
    // Abre a edição já no campo que falta (o formulário põe o foco nele ao terminar de abrir).
    onCompletePlace: (id, fieldKey) => {
      const p = acharLocal(id);
      if (p) { setCampoEmFoco(CAMPO_DO_FORM[fieldKey] || null); formDialog.handleOpen(p); }
    },
    onDelete: (id) => { const p = acharLocal(id); if (p) deleteDialog.handleOpen(p); },
    onManageTeam: (placeId) => { const p = acharLocal(placeId); if (p) professionalsDialog.handleOpen(p); },
    onConfigureProfessionalServices: (placeId, professionalId) => {
      const place = acharLocal(placeId);
      const placeProfessional = vinculoDoProfissional(place, professionalId);
      if (place && placeProfessional) professionalServicesDialog.handleOpen({ place, placeProfessional });
    },
    onManageCatalog: (placeId) => { const p = acharLocal(placeId); if (p) servicesDialog.handleOpen(p); },
    onConfigureServicePrices: (placeId, serviceId) => {
      const place = acharLocal(placeId);
      const link = (place?.services || []).find((s) => s.serviceId === serviceId);
      const service = (data.services || []).find((s) => s.id === serviceId);
      if (place && link) {
        paymentDialog.handleOpen({ ...link, service, serviceName: service?.name, price: service?.defaultPrice, placeId: place.id, placeName: place.name });
      }
    },
    onManagePayments: (placeId) => { const p = acharLocal(placeId); if (p) placePaymentsDialog.handleOpen(p); },
    onFix: (placeId, fix) => {
      switch (fix?.type) {
        case 'manageTeam': return actions.onManageTeam(placeId);
        case 'manageCatalog': return actions.onManageCatalog(placeId);
        case 'configureProfessionalServices': return actions.onConfigureProfessionalServices(placeId, fix.professionalId);
        case 'createSchedule': return navigate('/schedules?wizard=1');
        case 'openSchedule': return abrirAgendas(fix.scheduleId);
        case 'editAddress': return actions.onCompletePlace(placeId, 'address');
        default: return undefined;
      }
    },
    onOpenProfessional: () => navigate('/professionals'),
    onOpenSchedule: abrirAgendas,
    onBook: (placeId, professionalId, serviceId) => setAgendamento({ placeId, professionalId, serviceId }),
    onCopyAddress: async (placeId) => {
      const card = vm.items.find((c) => c.id === placeId) || vm.detail?.card;
      const endereco = [card?.address?.line1, card?.address?.line2].filter(Boolean).join(' — ');
      if (!endereco) return;
      try {
        await navigator.clipboard.writeText(endereco);
        showSuccess('Endereço copiado.');
      } catch {
        showError('Não foi possível copiar o endereço.');
      }
    },
    onOpenPage: (url) => { if (url) window.open(url, '_blank', 'noopener,noreferrer'); },
    // Mesmo destino do "Aguardando confirmação" da Home: é em Agendamentos que o dono confirma.
    onOpenAwaitingConfirmation: () => navigate('/appointments'),
  };

  return (
    <PlacesDataProvider data={data}>
      <PlacesView vm={vm} actions={actions} />

      {/* Cadastro e edição do local */}
      <PlaceForm open={formDialog.open} onClose={formDialog.handleClose} onSubmit={handleSubmit} data={formDialog.data} focusField={campoEmFoco} />

      {/* Preço de um serviço em cada forma de pagamento */}
      <PlaceServicePaymentDialog
        open={paymentDialog.open}
        onClose={paymentDialog.handleClose}
        placeService={paymentDialog.data}
        onUpdate={recarregarTudo}
      />

      {/* Exclusão do local, com o impacto antes */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess(`Exclusão de "${deleteDialog.data?.name}" concluída.`);
          setSelectedId(null);
          recarregarTudo();
        }}
        deletePath={`/places/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Catálogo do local */}
      <PlaceServicesManager open={servicesDialog.open} onClose={servicesDialog.handleClose} place={servicesDialog.data} onUpdate={recarregarTudo} />

      {/* Equipe do local */}
      <PlaceProfessionalsManager open={professionalsDialog.open} onClose={professionalsDialog.handleClose} place={professionalsDialog.data} onUpdate={recarregarTudo} />

      {/* Serviços de um profissional neste local */}
      <ProfessionalServicesDialog
        open={professionalServicesDialog.open}
        onClose={professionalServicesDialog.handleClose}
        placeProfessional={professionalServicesDialog.data?.placeProfessional}
        place={professionalServicesDialog.data?.place}
        onUpdate={recarregarTudo}
      />

      {/* Formas aceitas, com a confirmação do impacto de tirar */}
      <PlacePaymentsManagerDialog
        open={placePaymentsDialog.open}
        onClose={placePaymentsDialog.handleClose}
        place={placePaymentsDialog.data}
        onOpenCascadeConfirm={({ cascades, onConfirm }) => placePaymentsCascadeDialog.handleOpen({ cascades, onConfirm })}
        onUpdate={recarregarTudo}
      />
      <PlacePaymentCascadeConfirmDialog
        open={placePaymentsCascadeDialog.open}
        cascades={placePaymentsCascadeDialog.data?.cascades || []}
        onCancel={placePaymentsCascadeDialog.handleClose}
        onConfirm={() => {
          placePaymentsCascadeDialog.data?.onConfirm?.();
          placePaymentsCascadeDialog.handleClose();
        }}
      />

      {/* Cadastro guiado */}
      <SmartWizard
        open={showSmartWizard}
        wizardId="add-place"
        onClose={() => setShowSmartWizard(false)}
        onSuccess={() => {
          setShowSmartWizard(false);
          recarregarTudo();
          showSuccess('Cadastro criado.');
        }}
      />

      {/* Novo agendamento já com o local (e quem atende / o serviço, quando vier) */}
      <AppointmentFormDialog
        open={!!agendamento}
        onClose={() => setAgendamento(null)}
        onSuccess={() => { setAgendamento(null); showSuccess('Agendamento criado.'); recarregarTudo(); }}
        prefillConfig={agendamento}
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PlacesDataProvider>
  );
}
